import { useCallback, useEffect, useRef, useState } from 'react';

export const FEEDBACK_DICTATION_ACK_PREFIX = 'dha_prime_feedback_dictation_ack:v1:';

const ENVIRONMENT_DISABLED_MESSAGE =
  'Voice dictation is not available in this environment. You can still type your feedback.';
const BROWSER_UNSUPPORTED_MESSAGE =
  'Voice dictation is not available in this browser. You can still type your feedback.';
const PERMISSION_DENIED_MESSAGE =
  'Microphone permission was denied. You can still type your feedback.';
const SERVICE_BLOCKED_MESSAGE =
  'Voice dictation is blocked by your browser or organization. You can still type your feedback.';
const NO_SPEECH_MESSAGE = 'No speech was detected. Try again or type your feedback.';
const AUDIO_CAPTURE_MESSAGE =
  'No working microphone was found. Check your microphone connection and browser settings, then try again.';
const NETWORK_ERROR_MESSAGE =
  'Voice dictation could not reach the browser speech service. Check your connection, then try again or continue typing.';
const LANGUAGE_ERROR_MESSAGE =
  'Voice dictation could not start for English (United States). Check your browser language support or continue typing.';
const UNEXPECTED_ERROR_MESSAGE =
  'Voice dictation stopped unexpectedly. Your typed feedback is still available; try again or continue typing.';
const EMPTY_RESTART_MESSAGE =
  'Voice dictation paused because no speech was detected. Select Dictate feedback to try again.';

const MAX_EMPTY_RESTARTS = 3;
const EMPTY_RESTART_WINDOW_MS = 10_000;

function dictationEnabled(env) {
  return String(env?.VITE_ENABLE_FEEDBACK_DICTATION || '').toLowerCase() === 'true';
}

function getRecognitionConstructor(speechWindow) {
  return speechWindow?.SpeechRecognition || speechWindow?.webkitSpeechRecognition || null;
}

function getLocalStorage(speechWindow) {
  if (speechWindow?.localStorage) return speechWindow.localStorage;
  if (typeof window !== 'undefined') return window.localStorage;
  return null;
}

function acknowledgementKey(actorUserId) {
  return actorUserId ? `${FEEDBACK_DICTATION_ACK_PREFIX}${actorUserId}` : null;
}

function hasAcknowledged(actorUserId, speechWindow) {
  const key = acknowledgementKey(actorUserId);
  if (!key) return false;
  try {
    return getLocalStorage(speechWindow)?.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function saveAcknowledgement(actorUserId, speechWindow) {
  const key = acknowledgementKey(actorUserId);
  if (!key) return false;
  try {
    getLocalStorage(speechWindow)?.setItem(key, 'true');
    return true;
  } catch {
    return false;
  }
}

function normalizedTranscript(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function joinTranscript(left, right) {
  const first = normalizedTranscript(left);
  const second = normalizedTranscript(right);
  if (!first) return second;
  if (!second) return first;
  return `${first} ${second}`;
}

function insertTranscript(value, insertion, selectionStart, selectionEnd) {
  const transcript = normalizedTranscript(insertion);
  if (!transcript) return value;
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const leadingSpace = before && !/\s$/.test(before) && !/^[,.;:!?)]/.test(transcript) ? ' ' : '';
  const trailingSpace = after && !/^\s/.test(after) && !/^[,.;:!?)]/.test(after) ? ' ' : '';
  return `${before}${leadingSpace}${transcript}${trailingSpace}${after}`;
}

function messageForRecognitionError(code) {
  if (code === 'not-allowed') return PERMISSION_DENIED_MESSAGE;
  if (code === 'service-not-allowed') return SERVICE_BLOCKED_MESSAGE;
  if (code === 'no-speech') return NO_SPEECH_MESSAGE;
  if (code === 'audio-capture') return AUDIO_CAPTURE_MESSAGE;
  if (code === 'network') return NETWORK_ERROR_MESSAGE;
  if (code === 'language-not-supported' || code === 'bad-grammar') return LANGUAGE_ERROR_MESSAGE;
  return UNEXPECTED_ERROR_MESSAGE;
}

export function useFeedbackDictation({
  annotationId,
  actorUserId,
  enabled = true,
  active = true,
  noteRef,
  noteValue,
  onNoteValueChange,
  speechWindow = typeof window !== 'undefined' ? window : undefined,
  env = typeof import.meta !== 'undefined' ? import.meta.env : {},
}) {
  const recognitionRef = useRef(null);
  const noteValueRef = useRef(noteValue || '');
  const onNoteValueChangeRef = useRef(onNoteValueChange);
  const sessionRef = useRef(null);
  const desiredListeningRef = useRef(false);
  const launchRecognitionRef = useRef(null);
  const restartTimesRef = useRef([]);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [displayValue, setDisplayValue] = useState(null);
  const [consentRequired, setConsentRequired] = useState(false);
  const recognitionConstructor = getRecognitionConstructor(speechWindow);
  const availabilityMessage =
    !enabled || !dictationEnabled(env)
      ? ENVIRONMENT_DISABLED_MESSAGE
      : !recognitionConstructor
        ? BROWSER_UNSUPPORTED_MESSAGE
        : '';
  const canDictate = Boolean(active && actorUserId && !availabilityMessage);

  useEffect(() => {
    noteValueRef.current = noteValue || '';
    onNoteValueChangeRef.current = onNoteValueChange;
  }, [noteValue, onNoteValueChange]);

  const renderSessionValue = useCallback((session, interimOverride = null) => {
    if (!session) return;
    const interim = interimOverride == null ? session.interimText : interimOverride;
    const transcript = joinTranscript(session.committedText, interim);
    setDisplayValue(
      insertTranscript(
        session.baseValue,
        transcript,
        session.selectionStart,
        session.selectionEnd,
      ),
    );
  }, []);

  const commitSessionTranscript = useCallback((session, includeInterim = false) => {
    if (!session) return;
    if (includeInterim && session.interimText) {
      session.committedText = joinTranscript(session.committedText, session.interimText);
      session.interimText = '';
      session.interimByIndex.clear();
    }
    const nextValue = insertTranscript(
      session.baseValue,
      session.committedText,
      session.selectionStart,
      session.selectionEnd,
    );
    noteValueRef.current = nextValue;
    setDisplayValue(nextValue);
    onNoteValueChangeRef.current?.(nextValue);
    queueMicrotask(() => {
      const noteElement = noteRef?.current;
      const insertedLength = nextValue.length - session.baseValue.length +
        (session.selectionEnd - session.selectionStart);
      const caret = session.selectionStart + Math.max(0, insertedLength);
      noteElement?.focus?.();
      noteElement?.setSelectionRange?.(caret, caret);
    });
  }, [noteRef]);

  const cancelDictation = useCallback(() => {
    desiredListeningRef.current = false;
    launchRecognitionRef.current = null;
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    recognition?.abort?.();
    sessionRef.current = null;
    restartTimesRef.current = [];
    setConsentRequired(false);
    setDisplayValue(null);
    setStatus('idle');
  }, []);

  const stopDictation = useCallback(() => {
    desiredListeningRef.current = false;
    launchRecognitionRef.current = null;
    const session = sessionRef.current;
    if (session?.interimText) commitSessionTranscript(session, true);
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    recognition?.stop?.();
    sessionRef.current = null;
    restartTimesRef.current = [];
    setDisplayValue(null);
    setStatus('idle');
  }, [commitSessionTranscript]);

  const beginListening = useCallback(() => {
    if (!canDictate || !recognitionConstructor) return;
    const noteElement = noteRef?.current;
    const baseValue = noteValueRef.current;
    const selectionStart = Number.isInteger(noteElement?.selectionStart)
      ? noteElement.selectionStart
      : baseValue.length;
    const selectionEnd = Number.isInteger(noteElement?.selectionEnd)
      ? noteElement.selectionEnd
      : selectionStart;
    const session = {
      baseValue,
      selectionStart,
      selectionEnd,
      committedText: '',
      interimText: '',
      interimByIndex: new Map(),
      processedFinalIndexes: new Set(),
      recognitionHadActivity: false,
    };
    sessionRef.current = session;
    desiredListeningRef.current = true;
    restartTimesRef.current = [];
    setMessage('');

    const launchRecognition = () => {
      if (!desiredListeningRef.current || sessionRef.current !== session) return;
      const recognition = new recognitionConstructor();
      session.recognitionHadActivity = false;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.onstart = () => {
        if (recognitionRef.current !== recognition) return;
        setStatus('listening');
        setMessage('');
      };
      recognition.onresult = (event) => {
        if (recognitionRef.current !== recognition || sessionRef.current !== session) return;
        session.recognitionHadActivity = true;
        const results = Array.from(event?.results || []);
        const resultIndex = Number.isInteger(event?.resultIndex) ? event.resultIndex : 0;
        let finalized = false;
        for (let index = resultIndex; index < results.length; index += 1) {
          const speechResult = results[index];
          const transcript = normalizedTranscript(speechResult?.[0]?.transcript);
          if (speechResult?.isFinal) {
            session.interimByIndex.delete(index);
            if (transcript && !session.processedFinalIndexes.has(index)) {
              session.processedFinalIndexes.add(index);
              session.committedText = joinTranscript(session.committedText, transcript);
              finalized = true;
            }
          } else if (transcript) {
            session.interimByIndex.set(index, transcript);
          } else {
            session.interimByIndex.delete(index);
          }
        }
        session.interimText = [...session.interimByIndex.entries()]
          .sort(([left], [right]) => left - right)
          .map(([, transcript]) => transcript)
          .join(' ');
        if (finalized) commitSessionTranscript(session, false);
        renderSessionValue(session);
      };
      recognition.onerror = (event) => {
        if (recognitionRef.current !== recognition) return;
        recognitionRef.current = null;
        if (!desiredListeningRef.current && event?.error === 'aborted') return;
        desiredListeningRef.current = false;
        launchRecognitionRef.current = null;
        sessionRef.current = null;
        setDisplayValue(null);
        setStatus('idle');
        setMessage(messageForRecognitionError(event?.error));
      };
      recognition.onend = () => {
        if (recognitionRef.current === recognition) recognitionRef.current = null;
        if (!desiredListeningRef.current || sessionRef.current !== session) {
          setStatus('idle');
          return;
        }
        const now = Date.now();
        restartTimesRef.current = restartTimesRef.current.filter(
          (startedAt) => now - startedAt < EMPTY_RESTART_WINDOW_MS,
        );
        if (!session.recognitionHadActivity) restartTimesRef.current.push(now);
        if (restartTimesRef.current.length > MAX_EMPTY_RESTARTS) {
          desiredListeningRef.current = false;
          launchRecognitionRef.current = null;
          sessionRef.current = null;
          setDisplayValue(null);
          setStatus('idle');
          setMessage(EMPTY_RESTART_MESSAGE);
          return;
        }
        queueMicrotask(() => launchRecognitionRef.current?.());
      };
      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch (error) {
        recognitionRef.current = null;
        desiredListeningRef.current = false;
        launchRecognitionRef.current = null;
        sessionRef.current = null;
        setDisplayValue(null);
        setStatus('idle');
        setMessage(messageForRecognitionError(error?.name));
      }
    };

    launchRecognitionRef.current = launchRecognition;
    launchRecognition();
  }, [canDictate, commitSessionTranscript, noteRef, recognitionConstructor, renderSessionValue]);

  const startDictation = useCallback(() => {
    if (!active) return;
    if (!enabled || !dictationEnabled(env)) {
      setStatus('idle');
      setMessage(ENVIRONMENT_DISABLED_MESSAGE);
      return;
    }
    if (!recognitionConstructor) {
      setStatus('idle');
      setMessage(BROWSER_UNSUPPORTED_MESSAGE);
      return;
    }
    if (!actorUserId) {
      setStatus('idle');
      setMessage(ENVIRONMENT_DISABLED_MESSAGE);
      return;
    }
    if (!hasAcknowledged(actorUserId, speechWindow)) {
      setConsentRequired(true);
      setMessage('');
      return;
    }
    beginListening();
  }, [active, actorUserId, beginListening, enabled, env, recognitionConstructor, speechWindow]);

  const acknowledgeConsent = useCallback(() => {
    if (!actorUserId) return;
    saveAcknowledgement(actorUserId, speechWindow);
    setConsentRequired(false);
    beginListening();
  }, [actorUserId, beginListening, speechWindow]);

  const cancelConsent = useCallback(() => {
    setConsentRequired(false);
    setMessage('Voice dictation was not started. You can continue typing your feedback.');
  }, []);

  useEffect(() => {
    if (!active) cancelDictation();
  }, [active, cancelDictation]);

  useEffect(() => {
    cancelDictation();
  }, [actorUserId, annotationId, cancelDictation]);

  useEffect(() => () => cancelDictation(), [cancelDictation]);

  return {
    status,
    message,
    displayValue,
    consentRequired,
    availabilityMessage,
    canDictate,
    startDictation,
    stopDictation,
    acknowledgeConsent,
    cancelConsent,
  };
}
